/* eslint-disable camelcase */
import React, { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import {
  H2,
  H4,
  P,
  Modal,
  ModalHeader,
  ModalBody,
  Icon,
  Grid,
  Header,
  FlexRow,
  BaseCol,
  FlexBaseCol,
  Subtext,
  ProgressBar
} from '@contently/toolkit';
import styled from '@emotion/styled';
import CalculationBox from './calculation_box';
import Filter from './filter';
import filterHelper from './filterHelper';
import { removeCentsAddCommas, firstDate, lastDate } from './utils';

function ValueWrapper({ appData }) {
  const { pub_info: pubInfo, value_info: valueInfo } = JSON.parse(appData);
  const [modal, setModal] = useState(false);
  const [filterState, setFilterState] = useState(
    localStorage.getItem('contentValueRegion') || 'All'
  );

  const {
    total_organic_search_traffic: totalOrganicSearchTraffic,
    total_organic_search_traffic_value: totalOrganicSearchTrafficValue,
    total_content_debit: totalContentDebit,
    average_cpc: averageCpc
  } = filterHelper(valueInfo, filterState);

  const {
    pub_id: pubId,
    completed_pubs: completedPubs,
    published_pubs: publishedPubs,
    first_requested_at: firstRequested,
    last_requested_at: lastRequested
  } = pubInfo;

  const percentPublished = (publishedPubs / completedPubs) * 100;

  const GrayBorderDiv = styled('div')`
    border: 1px solid #d4d4d4;
    padding: 24px 20px;
    margin: 0 18px 0 0;
    height: 100%;
    @media screen and (max-width: 768px) {
      margin-right: 0;
    }
  `;

  const ShadedGrayBorderDiv = styled(GrayBorderDiv)`
    background-color: #f8f8f8;
    flex-grow: 1;
  `;

  const BaseColSpace = styled(BaseCol)`
    margin-bottom: 20px;
  `;

  const DarkGrayBorderDiv = styled(GrayBorderDiv)`
    border: 1px solid #999;
  `;

  const ProgressDiv = styled(GrayBorderDiv)`
    border-color: #efefef;
    background-color: #fcfcfc;
  `;

  const SectionTitle = styled('div')`
    margin: 20px 0 14px 0;
  `;

  const TimeframeDiv = styled('div')`
    text-align: right;
    margin: 0 18px 15px 0;
    @media screen and (max-width: 768px) {
      text-align: left;
    }
  `;

  const FilterDiv = styled('div')`
    text-align: right;
  `;

  const incomplete =
    publishedPubs / completedPubs !== 1 ? (
      <FlexRow>
        <BaseColSpace>
          <ProgressDiv>
            <H4>Improve your content value</H4>
            <ProgressBar percent={percentPublished} />
            <P style={{ marginTop: '10px', fontSize: '16px' }}>
              {publishedPubs.toLocaleString()} published stories are included in the calculations
              below, but you have {completedPubs.toLocaleString()} stories completed. Mark{' '}
              <a
                className="roi-link"
                href={`${window.origin}/stories/library?hide_filters=false&publications%5B%5D=${pubId}&content_value=none`}
              >
                these stories
              </a>{' '}
              published (and add URLs) to ensure they&apos;re included next month
            </P>
          </ProgressDiv>
        </BaseColSpace>
      </FlexRow>
    ) : (
      ''
    );

  return (
    <Fragment>
      <Modal isOpen={modal} onClose={() => setModal(false)}>
        <ModalHeader>
          <H2>Content Value</H2>
        </ModalHeader>
        <ModalBody>
          <P>
            This dashboard measures how much value your published content has generated to date. ROI
            is calculated by subtracting spend on content from traffic value. Values are updated
            every 30 days. To learn more about how we calculate ROI, visit this article in
            Contently&apos;s help center.
          </P>
        </ModalBody>
      </Modal>
      <Grid>
        <FlexRow>
          <BaseCol md={5}>
            <Header>
              <H2>
                Content Value
                <Icon type="question-circle" onClick={() => setModal(true)} />
              </H2>
            </Header>
          </BaseCol>
          <BaseCol md={5}>
            <TimeframeDiv>
              <P style={{ fontSize: '12px' }}>
                {firstDate(firstRequested)} - {lastDate(lastRequested)}
                <br />
                Dashboard updates automatically at the end of each month
              </P>
            </TimeframeDiv>
          </BaseCol>
          <FlexBaseCol>
            <FilterDiv>
              <Filter filterState={filterState} setFilterState={setFilterState} />
            </FilterDiv>
          </FlexBaseCol>
        </FlexRow>
        {incomplete}
        <FlexRow>
          <BaseCol>
            <SectionTitle>
              <H4>Overview</H4>
            </SectionTitle>
          </BaseCol>
        </FlexRow>
        <FlexRow>
          <BaseColSpace>
            <DarkGrayBorderDiv>
              <CalculationBox
                id="total-organic-search-roi"
                title="SEO Traffic Value"
                subtext="The total cost to acquire your organic search traffic and keyword rankings with Google Ads."
                value={`$${removeCentsAddCommas(totalOrganicSearchTrafficValue)}`}
              />
            </DarkGrayBorderDiv>
          </BaseColSpace>
        </FlexRow>
        <FlexRow>
          <BaseColSpace>
            <ShadedGrayBorderDiv>
              <P style={{ fontSize: '16px' }}>
                <Icon type="lightbulb" style={{ marginRight: '10px' }} /> Interested in learning
                more about how we calculate value?{' '}
                <a
                  className="roi-link"
                  href="https://help.contently.com/en/articles/4273934-understanding-and-improving-your-roi-dashboard"
                >
                  Read our guide
                </a>
                .
              </P>
            </ShadedGrayBorderDiv>
          </BaseColSpace>
        </FlexRow>
        <FlexRow>
          <BaseColSpace md={6}>
            <GrayBorderDiv>
              <CalculationBox
                id="total-traffic-value"
                title="ASSOC. CONTENT SPEND"
                subtext={
                  <Subtext style={{ lineHeight: '18px' }}>
                    The total amount spent on freelancer payments for all stories included in the
                    calculation.
                  </Subtext>
                }
                value={`$${removeCentsAddCommas(totalContentDebit)}`}
              />
            </GrayBorderDiv>
          </BaseColSpace>
          <BaseColSpace md={6}>
            <GrayBorderDiv>
              <CalculationBox
                id="total-content-spend"
                title="NET SEO TRAFFIC VALUE"
                subtext={
                  <Subtext style={{ lineHeight: '18px' }}>
                    The total traffic value remaining after subtracting content spend from
                    associated stories.
                  </Subtext>
                }
                value={`$${removeCentsAddCommas(
                  totalOrganicSearchTrafficValue - totalContentDebit
                )}`}
              />
            </GrayBorderDiv>
          </BaseColSpace>
        </FlexRow>
        <FlexRow>
          <BaseCol>
            <SectionTitle>
              <H4>Traffic value calculations</H4>
            </SectionTitle>
          </BaseCol>
        </FlexRow>
        <FlexRow>
          <BaseColSpace md={6} xl={3}>
            <GrayBorderDiv>
              <CalculationBox
                id="average-cost-per-click"
                title="AVERAGE CPC"
                subtext="The average cost-per-click associated with keywords that your content ranks for organically."
                value={`$${averageCpc.toFixed(2).toLocaleString()}`}
              />
            </GrayBorderDiv>
          </BaseColSpace>
          <BaseColSpace md={6} xl={3}>
            <GrayBorderDiv>
              <CalculationBox
                id="total-traffic"
                title="ORGANIC SEARCH TRAFFIC"
                subtext="The number of visitors who accessed your content via Google’s search engine results pages."
                value={totalOrganicSearchTraffic.toLocaleString()}
              />
            </GrayBorderDiv>
          </BaseColSpace>
        </FlexRow>
      </Grid>
    </Fragment>
  );
}

export default ValueWrapper;

ValueWrapper.propTypes = {
  appData: PropTypes.string.isRequired
};



// WEBPACK FOOTER //
// ./app/javascript/app/components/value/value_wrapper.jsx